import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, Save } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/Skeleton'
import { useAuth } from '../../contexts/AuthContext'
import { useExercises } from '../../hooks/useExercises'
import { useWorkoutTemplates } from '../../hooks/useWorkoutTemplates'
import { useSession } from '../../hooks/useSession'
import { useSessionDetail } from '../../hooks/useSessionDetail'
import { useProfile } from '../../hooks/useProfile'
import WorkoutBackHeader from './components/WorkoutBackHeader'
import SessionTypeSelector from './components/SessionTypeSelector'
import SetLogger from './components/SetLogger'
import CardioBlock from './components/CardioBlock'
import SessionMetaForm from './components/SessionMetaForm'
import ExercisePicker from './components/ExercisePicker'
import CardioPresetPicker from './components/CardioPresetPicker'

// Build a fresh-from-saved-session draft for edit mode. Mirrors the
// shape that emptyDraft() produces so the rest of the form is agnostic
// to whether it's editing or creating.
function draftFromSession(session) {
  if (!session) return null
  return {
    sessionType: session.session_type,
    date: session.date,
    logs: (session.exercise_logs || []).map((l, idx) => ({
      clientId: crypto.randomUUID(),
      exerciseId: l.exercise_id,
      order_index: l.order_index ?? idx,
      notes: l.notes || '',
      sets: (l.sets || []).map((s) => ({
        weight_kg: s.weight_kg != null ? String(s.weight_kg) : '',
        reps: s.reps != null ? String(s.reps) : '',
        rpe: s.rpe != null ? String(s.rpe) : '',
        is_warmup: !!s.is_warmup,
        is_failure: !!s.is_failure,
      })),
    })),
    cardios: (session.cardio_logs || []).map((c, idx) => ({
      clientId: crypto.randomUUID(),
      cardio_type: c.cardio_type,
      order_index: c.order_index ?? idx,
      duration_min: c.duration_min != null ? String(c.duration_min) : '',
      level: c.level != null ? String(c.level) : '',
      incline: c.incline != null ? String(c.incline) : '',
      speed: c.speed != null ? String(c.speed) : '',
      distance_km: c.distance_km != null ? String(c.distance_km) : '',
      calories_estimated: c.calories_estimated != null ? String(c.calories_estimated) : '',
      notes: c.notes || '',
    })),
    meta: {
      body_weight_kg: session.body_weight_kg != null ? String(session.body_weight_kg) : '',
      energy_rating: session.energy_rating != null ? String(session.energy_rating) : '',
      start_time: session.start_time || '',
      duration_min: session.duration_min != null ? String(session.duration_min) : '',
      notes: session.notes || '',
    },
  }
}

const DRAFT_KEY = 'workout-draft'

function emptyDraft() {
  return {
    sessionType: null,
    date: new Date().toISOString().slice(0, 10),
    logs: [],
    cardios: [],
    meta: {
      body_weight_kg: '',
      energy_rating: '',
      start_time: '',
      duration_min: '',
      notes: '',
    },
  }
}

// Best-effort persistence — silent on quota errors so the flow never breaks.
function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch {
    return null
  }
}
function saveDraft(draft) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(draft)) } catch { /* ignore */ }
}
function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY) } catch { /* ignore */ }
}

function isDraftEmpty(d) {
  if (!d) return true
  if (d.sessionType) return false
  if ((d.logs || []).length) return false
  if ((d.cardios || []).length) return false
  return true
}

const TEMPLATE_KEYS = {
  day1_chest_back: 'day1_chest_back',
  day2_arms_shoulders: 'day2_arms_shoulders',
  day3_legs: 'day3_legs',
}

export default function NewSession() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editSessionId = searchParams.get('edit')
  const isEditing = !!editSessionId

  const { user } = useAuth()
  const { exercises, loading: exercisesLoading } = useExercises()
  const { templates } = useWorkoutTemplates()
  const { saveSession, updateSession, saving, error: saveError } = useSession()
  const { profile } = useProfile()
  // In edit mode pull the existing session and hydrate the draft from
  // it. The hook is a no-op when editSessionId is null/undefined.
  const { session: editingSession, loading: editingLoading } = useSessionDetail(editSessionId)

  const [draft, setDraft] = useState(emptyDraft)
  const [resumePending, setResumePending] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [validationError, setValidationError] = useState(null)
  const hydrated = useRef(false)

  // Edit-mode hydration: when the session loads, copy it into the draft
  // once. Skips the draft-resume prompt since edit context overrides any
  // stored new-session draft.
  useEffect(() => {
    if (!isEditing) return
    if (hydrated.current) return
    if (!editingSession) return
    const next = draftFromSession(editingSession)
    if (next) {
      setDraft(next)
      hydrated.current = true
      setResumePending(false)
    }
  }, [isEditing, editingSession])

  // New-session resume prompt — only runs when NOT in edit mode.
  useEffect(() => {
    if (isEditing) return
    if (hydrated.current) return
    hydrated.current = true
    const stored = loadDraft()
    if (!isDraftEmpty(stored)) setResumePending(true)
  }, [isEditing])

  // Body weight used by the cardio-block calorie estimator. Priority:
  // session-specific override (meta.body_weight_kg) → profile start
  // weight → safe 70 kg fallback so the estimator always has a number.
  const effectiveBodyWeight = useMemo(() => {
    const fromMeta = Number(draft.meta?.body_weight_kg)
    if (Number.isFinite(fromMeta) && fromMeta > 0) return fromMeta
    const fromProfile = Number(profile?.start_weight)
    if (Number.isFinite(fromProfile) && fromProfile > 0) return fromProfile
    return 70
  }, [draft.meta?.body_weight_kg, profile?.start_weight])

  // Persist on every change (debounce to avoid hammering localStorage).
  // Disabled in edit mode — we don't want the edit draft to spill into
  // the next new-session resume prompt.
  useEffect(() => {
    if (isEditing) return
    if (resumePending) return
    if (isDraftEmpty(draft)) return
    const id = setTimeout(() => saveDraft(draft), 400)
    return () => clearTimeout(id)
  }, [draft, resumePending, isEditing])

  // Map exercise ID → exercise row (memoised) for SetLogger headers.
  const exercisesById = useMemo(() => {
    const m = new Map()
    for (const e of exercises) m.set(e.id, e)
    return m
  }, [exercises])

  const pickType = useCallback((type) => {
    const templateKey = TEMPLATE_KEYS[type]
    let logs = []
    if (templateKey) {
      const ids = templates[templateKey] || []
      // Filter out template entries whose exercises no longer exist (archived/deleted).
      const validIds = ids.filter((id) => exercisesById.has(id))
      logs = validIds.map((id, idx) => ({
        clientId: crypto.randomUUID(),
        exerciseId: id,
        order_index: idx,
        notes: '',
        sets: [],
      }))
    }
    // Swim sessions auto-add a swim cardio block so the user only fills
    // in duration + distance rather than discovering "add cardio" first.
    const cardios =
      type === 'swim'
        ? [
            {
              clientId: crypto.randomUUID(),
              cardio_type: 'swim',
              order_index: 0,
              duration_min: '',
              level: '',
              incline: '',
              speed: '',
              distance_km: '',
              calories_estimated: '',
              notes: '',
            },
          ]
        : []
    setDraft((d) => ({ ...d, sessionType: type, logs, cardios }))
  }, [templates, exercisesById])

  const addExercise = useCallback((exerciseId) => {
    setDraft((d) => ({
      ...d,
      logs: [
        ...d.logs,
        {
          clientId: crypto.randomUUID(),
          exerciseId,
          order_index: d.logs.length,
          notes: '',
          sets: [],
        },
      ],
    }))
    setPickerOpen(false)
  }, [])

  const updateLog = useCallback((clientId, patch) => {
    setDraft((d) => ({
      ...d,
      logs: d.logs.map((l) => (l.clientId === clientId ? { ...l, ...patch } : l)),
    }))
  }, [])

  const removeLog = useCallback((clientId) => {
    setDraft((d) => ({
      ...d,
      logs: d.logs.filter((l) => l.clientId !== clientId).map((l, idx) => ({ ...l, order_index: idx })),
    }))
  }, [])

  const moveLog = useCallback((clientId, direction) => {
    setDraft((d) => {
      const idx = d.logs.findIndex((l) => l.clientId === clientId)
      if (idx < 0) return d
      const target = direction === 'up' ? idx - 1 : idx + 1
      if (target < 0 || target >= d.logs.length) return d
      const next = [...d.logs]
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return { ...d, logs: next.map((l, i) => ({ ...l, order_index: i })) }
    })
  }, [])

  const addCardio = useCallback(() => {
    setDraft((d) => ({
      ...d,
      cardios: [
        ...d.cardios,
        {
          clientId: crypto.randomUUID(),
          cardio_type: 'elliptical',
          order_index: d.cardios.length,
          duration_min: '',
          level: '',
          incline: '',
          speed: '',
          distance_km: '',
          calories_estimated: '',
          notes: '',
        },
      ],
    }))
  }, [])

  const updateCardio = useCallback((clientId, patch) => {
    setDraft((d) => ({
      ...d,
      cardios: d.cardios.map((c) => (c.clientId === clientId ? { ...c, ...patch } : c)),
    }))
  }, [])

  const removeCardio = useCallback((clientId) => {
    setDraft((d) => ({
      ...d,
      cardios: d.cardios.filter((c) => c.clientId !== clientId).map((c, idx) => ({ ...c, order_index: idx })),
    }))
  }, [])

  const updateMeta = useCallback((patch) => {
    setDraft((d) => ({ ...d, meta: { ...d.meta, ...patch } }))
  }, [])

  const validate = () => {
    setValidationError(null)
    if (!draft.sessionType) return t('workout.session.pickType')
    if (draft.logs.length === 0 && draft.cardios.length === 0) {
      return t('workout.session.emptyError')
    }
    for (const log of draft.logs) {
      for (const s of log.sets) {
        if (s.is_warmup) continue
        if (!Number(s.reps) || Number(s.reps) <= 0) return t('workout.set.invalidReps')
      }
    }
    for (const c of draft.cardios) {
      if (!Number(c.duration_min) || Number(c.duration_min) <= 0) return t('workout.cardio.invalidDuration')
    }
    return null
  }

  const handleSave = async () => {
    const msg = validate()
    if (msg) { setValidationError(msg); return }
    const res = isEditing
      ? await updateSession(editSessionId, draft)
      : await saveSession(draft)
    if (!res.ok) {
      setValidationError(t('workout.session.saveError', { message: res.error }))
      return
    }
    if (!isEditing) clearDraft()
    if (isEditing) {
      navigate(`/workout/sessions/${editSessionId}`, {
        state: { toast: t('workout.session.updatedToast') },
      })
    } else {
      navigate('/workout', { state: { toast: t('workout.session.savedToast') } })
    }
  }

  // Resume / discard prompt — shown before any draft hydration.
  if (resumePending) {
    return (
      <div className="space-y-4 content-fade">
        <WorkoutBackHeader title={t('workout.session.title')} fallback="/workout" />
        <Card>
          <CardContent className="p-5 space-y-3">
            <h2 className="text-base font-semibold text-foreground">
              {t('workout.session.resumePromptTitle')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('workout.session.resumePromptDesc')}
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  const stored = loadDraft()
                  if (stored) setDraft(stored)
                  setResumePending(false)
                }}
                className="bg-app-primary-500 hover:bg-app-primary-600 text-white"
              >
                {t('workout.session.resume')}
              </Button>
              <Button
                variant="outline"
                onClick={() => { clearDraft(); setResumePending(false) }}
              >
                {t('workout.session.discard')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Edit mode: wait for the session to load before showing the form so
  // we don't briefly render the empty new-session state.
  if (exercisesLoading || (isEditing && editingLoading && !hydrated.current)) {
    return (
      <div className="space-y-4 content-fade">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-56 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-4 content-fade">
      <WorkoutBackHeader
        title={isEditing ? t('workout.session.editTitle') : t('workout.session.title')}
        fallback={isEditing ? `/workout/sessions/${editSessionId}` : '/workout'}
      />

      {/* Step 1: type picker (always visible — picking another type re-seeds logs) */}
      <SessionTypeSelector value={draft.sessionType} onPick={pickType} />

      {draft.sessionType && (
        <>
          {/* Exercise logs */}
          {draft.logs.map((log, idx) => (
            <SetLogger
              key={log.clientId}
              log={log}
              exercise={exercisesById.get(log.exerciseId)}
              onChange={(patch) => updateLog(log.clientId, patch)}
              onRemove={() => removeLog(log.clientId)}
              onMoveUp={idx > 0 ? () => moveLog(log.clientId, 'up') : null}
              onMoveDown={idx < draft.logs.length - 1 ? () => moveLog(log.clientId, 'down') : null}
            />
          ))}

          {/* Add exercise */}
          <Button
            variant="outline"
            onClick={() => setPickerOpen(true)}
            className="w-full"
          >
            + {t('workout.session.addExercise')}
          </Button>

          {/* Cardio presets — quick-load common interval plans. Available
              on every session type so e.g. a Zone 2 plan can be appended
              before the lifts on a strength day. */}
          <CardioPresetPicker
            onLoad={(blocks) => {
              setDraft((d) => ({
                ...d,
                cardios: [
                  ...d.cardios,
                  ...blocks.map((b, idx) => ({ ...b, order_index: d.cardios.length + idx })),
                ],
              }))
            }}
          />

          {/* Cardio blocks */}
          {draft.cardios.map((c) => (
            <CardioBlock
              key={c.clientId}
              cardio={c}
              bodyWeightKg={effectiveBodyWeight}
              onChange={(patch) => updateCardio(c.clientId, patch)}
              onRemove={() => removeCardio(c.clientId)}
            />
          ))}

          {/* Add cardio */}
          <Button
            variant="outline"
            onClick={addCardio}
            className="w-full"
          >
            + {t('workout.session.addCardio')}
          </Button>

          {/* Session-level meta */}
          <SessionMetaForm meta={draft.meta} onChange={updateMeta} userId={user?.id} />

          {/* Errors */}
          {(validationError || saveError) && (
            <p className="text-sm text-red-500 text-center">
              {validationError || saveError}
            </p>
          )}

          {/* Save */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full h-12 bg-app-primary-500 hover:bg-app-primary-600 text-white"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 animate-spin me-2" /> {t('workout.session.saving')}</>
            ) : (
              <><Save className="w-4 h-4 me-2" /> {t('workout.session.save')}</>
            )}
          </Button>
        </>
      )}

      {/* Bottom-sheet picker */}
      {pickerOpen && (
        <ExercisePicker
          exercises={exercises}
          excludeIds={draft.logs.map((l) => l.exerciseId)}
          onPick={addExercise}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  )
}
